import { clientVersion } from '@trello/config';
import { trelloFetch } from '@trello/fetch';
import { token } from '@trello/session-cookie';
// eslint-disable-next-line no-restricted-imports
import { normalizeNotification } from 'app/gamma/src/api/normalizers/notification';
// eslint-disable-next-line no-restricted-imports
import type { DeletedModel } from 'app/gamma/src/modules/sockets';
// eslint-disable-next-line no-restricted-imports
import { isDeletedModel } from 'app/gamma/src/modules/sockets';
import { isFilteringByUnreadNotifications } from 'app/src/components/NotificationsMenu/notificationsMenuState';
// eslint-disable-next-line no-restricted-imports
import type { NotificationResponse } from 'app/gamma/src/types/responses';
// eslint-disable-next-line no-restricted-imports
import type {
  NotificationGroupModel,
  NotificationsCountModel,
} from 'app/gamma/src/types/models';
import { sortBy } from 'underscore';
import { setNotificationSeenStateGroupCount } from 'app/src/components/NotificationsMenu/notificationSeenState';
import { groupNotifications } from 'app/src/components/NotificationsMenu/groupNotifications';
import { setNotificationsInGroupsRead } from 'app/src/components/NotificationsMenu/setNotificationsInGroupsRead';
import { notificationsState } from 'app/src/components/NotificationsMenu/notificationsState';

// Takes an id like Card:5c2d1616e23b680add1cf24d and converts it
// to the id form which would be 5c2d1616e23b680add1cf24d removing
// the type prefix of Card
const convertIdGroupToId = (idGroup: string) => idGroup.split(':')[1];

// We take the input from the responses that might have be received from
// server and compare that with what we already have, if the notification
// group already exists we replace it, then finally if we are filtering
// by unread make sure to remove any read only groups from the source.
// Finally we sort these to make sure they are always returned in the
// correct order
export const sanitizeNotificationGroups = (
  existing: NotificationGroupModel[],
  incoming: NotificationGroupModel[],
  isFilteringByUnread: boolean,
): NotificationGroupModel[] => {
  const mergedGroups = existing.map((existingGroup) => {
    const duplicate = incoming.find(
      (incomingGroup) => incomingGroup.id === existingGroup.id,
    );

    return duplicate ? duplicate : existingGroup;
  });

  const newGroups = incoming.filter((potentialNewGroup) => {
    const duplicate = existing.find(
      (existingGroup) => existingGroup.id === potentialNewGroup.id,
    );

    return !duplicate;
  });

  // append remaining incoming groups
  const joinedGroups =
    newGroups.length > 0 ? [...mergedGroups, ...newGroups] : mergedGroups;

  const filteredGroups = joinedGroups.filter((notificationGroup) =>
    isFilteringByUnread
      ? notificationGroup.notifications.some(
          (notification) => !!notification.unread,
        )
      : true,
  );

  // Sort the notifications to confirm the order is correct
  const sortedNotifications = filteredGroups.map((notificationGroup) => ({
    ...notificationGroup,
    notifications: sortBy(notificationGroup.notifications, (notification) =>
      notification.date ? new Date(notification.date).getTime() : 0,
    ),
  }));

  // We sort the group by the date of the last notification in the
  // group, which will be the newest.
  return sortBy(sortedNotifications, (notificationGroup) => {
    const lastNotification = notificationGroup.notifications.slice(-1)[0];

    return (
      lastNotification &&
      lastNotification.date &&
      -new Date(lastNotification.date).getTime()
    );
  });
};

export const updateUnreadCount = (
  state: NotificationsCountModel,
  idGroup: string,
  direction: number,
) => {
  const { [idGroup]: count, ...rest } = state;
  const newCount = (count || 0) + direction;
  const newCountGroup =
    newCount > 0
      ? {
          ...rest,
          [idGroup]: newCount,
        }
      : rest;

  return newCountGroup;
};

const getNotificationById = (idNotification: string) => {
  for (const notificationGroup of notificationsState.value.notificationGroups) {
    for (const notification of notificationGroup.notifications) {
      if (notification.id === idNotification) {
        return notification;
      }
    }
  }
};

export const addOrUpdateNotification = (
  notificationGroups: NotificationGroupModel[],
  idGroup: string,
  delta: NotificationResponse,
) => {
  // Find existing group and notification if they exist.
  const existingGroup = notificationGroups.find(
    (notificationGroup) => notificationGroup.idGroup === idGroup,
  );

  const existingNotification =
    existingGroup &&
    existingGroup.notifications.find(
      (notification) => notification.id === delta.id,
    );

  // If we have an existing group and an existing notifications lets find
  // that notification and update it, if we don't have an existing
  // notification then add the notification to the the existing group and
  // if we don't have an existing group create a new one with the new
  // notification.
  const newNotifications = existingGroup
    ? existingNotification
      ? existingGroup.notifications.map((notification) =>
          notification.id === delta.id
            ? normalizeNotification(delta, notification)
            : notification,
        )
      : [...existingGroup.notifications, normalizeNotification(delta)]
    : [normalizeNotification(delta)];

  // Take new notifications and create a group from them.
  const newGroup = groupNotifications(newNotifications)[0];

  if (newGroup === undefined) {
    // When does this happen?
    // https://trello.atlassian.net/projects/TRELP/queues/issue/TRELP-1852
    return notificationGroups;
  }

  // Either put the notification into an existing group, or create a new group
  return existingGroup !== undefined
    ? notificationGroups.map(
        (notificationGroup): NotificationGroupModel =>
          notificationGroup.idGroup === idGroup ? newGroup : notificationGroup,
      )
    : [newGroup, ...notificationGroups];
};

// Look through the notification groups and their notifications
// and remove the given notification from the data.
export const removeNotification = (
  notificationGroups: NotificationGroupModel[],
  id: string,
  delta: DeletedModel,
) =>
  notificationGroups.map((notificationGroup) => ({
    ...notificationGroup,
    notifications: notificationGroup.notifications.filter(
      (notification) => notification.id !== delta.id,
    ),
  }));

export const setNotificationGroupReadRequest = ({
  idGroup,
  read,
  notificationIds,
}: {
  idGroup: string;
  read: boolean;
  notificationIds?: string[];
}) => {
  const id = convertIdGroupToId(idGroup);

  let numNotifications = 0;
  const existingGroup = notificationsState.value.notificationGroups.find(
    (notificationGroup) => notificationGroup.idGroup === idGroup,
  );
  if (existingGroup) {
    for (const notification of existingGroup.notifications) {
      if (
        (!notificationIds || notificationIds.includes(notification.id)) &&
        notification.unread === read
      ) {
        numNotifications++;
      }
    }
  }
  const unreadDelta = read ? -numNotifications : numNotifications;
  const newUnreadCount = updateUnreadCount(
    notificationsState.value.unreadCount,
    idGroup,
    unreadDelta,
  );

  notificationsState.setValue({
    unreadCount: newUnreadCount,
    seenUnreadCount: newUnreadCount,
    notificationGroups: setNotificationsInGroupsRead(
      notificationsState.value.notificationGroups,
      read,
      id,
      notificationIds,
    ),
  });
};

const setNotificationGroupReadSuccess = ({
  isFilteringByUnread,
  idGroup,
  read,
}: {
  isFilteringByUnread: boolean;
  idGroup: string;
  read: boolean;
}) => {
  const id = convertIdGroupToId(idGroup);

  if (isFilteringByUnread && read) {
    // If we mark a notification group as read and we
    // are filtering, remove the notification group
    notificationsState.setValue({
      ...notificationsState.value,
      notificationGroups: notificationsState.value.notificationGroups.filter(
        (notificationGroup) => notificationGroup.id !== id,
      ),
    });
  }
};

const setAllNotificationsReadRequest = () => {
  notificationsState.setValue({
    ...notificationsState.value,
    unreadCount: {},
    notificationGroups: setNotificationsInGroupsRead(
      notificationsState.value.notificationGroups,
      true,
    ),
  });
};

const setAllNotificationsReadSuccess = ({
  isFilteringByUnread,
}: {
  isFilteringByUnread: boolean;
}) => {
  // If we mark all notifications as read and we are
  // filtering, clear them to update the ui to show sleepy taco.
  // We blank the array to match what the server would return.
  if (isFilteringByUnread) {
    notificationsState.setValue({
      ...notificationsState.value,
      notificationGroups: [],
    });
  }
};

const setAllNotificationsReadError = ({
  unreadCount,
  ids,
}: {
  unreadCount: NotificationsCountModel;
  ids: string[];
}) => {
  notificationsState.setValue({
    ...notificationsState.value,
    unreadCount,
    notificationGroups: setNotificationsInGroupsRead(
      notificationsState.value.notificationGroups,
      false,
      ids,
    ),
  });
};

export const setSeenUnreadNotifications = (
  seenUnreadCount: NotificationsCountModel,
) => {
  notificationsState.setValue({
    ...notificationsState.value,
    seenUnreadCount,
  });
};

export const applySocketNotification = ({
  delta,
  isFilteringByUnread,
}: {
  delta: DeletedModel | NotificationResponse;
  isFilteringByUnread: boolean;
}) => {
  let notificationGroups = notificationsState.value.notificationGroups;
  let unreadCount = notificationsState.value.unreadCount;

  if (isDeletedModel(delta)) {
    const existingNotification = getNotificationById(delta.id);

    if (existingNotification) {
      notificationGroups = removeNotification(
        notificationsState.value.notificationGroups,
        delta.id,
        delta,
      );

      if (existingNotification.unread) {
        const idGroup =
          existingNotification.data && existingNotification.data.card
            ? `Card:${existingNotification.data.card.id}`
            : `Notification:${delta.id}`;

        unreadCount = updateUnreadCount(unreadCount, idGroup, -1);
      }
    }
  } else {
    // Create idGroup for notification
    const idGroup =
      delta.data && delta.data.card
        ? `Card:${delta.data.card.id}`
        : `Notification:${delta.id}`;

    const existingNotification = getNotificationById(delta.id);

    if (existingNotification && existingNotification.unread !== delta.unread) {
      unreadCount = updateUnreadCount(
        unreadCount,
        idGroup,
        existingNotification.unread ? -1 : 1,
      );
    } else if (!existingNotification && delta.unread) {
      unreadCount = updateUnreadCount(unreadCount, idGroup, 1);
    }

    notificationGroups = addOrUpdateNotification(
      notificationsState.value.notificationGroups,
      idGroup,
      delta,
    );
  }

  notificationsState.setValue({
    ...notificationsState.value,
    unreadCount,
    notificationGroups: sanitizeNotificationGroups(
      notificationsState.value.notificationGroups,
      notificationGroups,
      isFilteringByUnread,
    ),
  });
};

const readAllNotifications = async (
  body: { ids: string; read: boolean } | object = {},
) =>
  trelloFetch(
    '/1/notifications/all/read',
    {
      headers: { 'Content-Type': 'application/json' },
      method: 'POST',
      body: JSON.stringify({
        ...body,
        token,
      }),
    },
    {
      clientVersion,
      networkRequestEventAttributes: {
        source: 'trelloFetch',
        operationName: 'readAllNotifications',
      },
    },
  );

export const setAllNotificationsRead = async () => {
  // in case we need to revert
  const idUnreadNotificationGroups = notificationsState.value.notificationGroups
    .filter((notificationGroup: NotificationGroupModel) =>
      notificationGroup.notifications.some((n) => !!n.unread),
    )
    .map((ng) => ng.id);

  const unreadCountGroup = notificationsState.value.unreadCount;

  try {
    setAllNotificationsReadRequest();

    await readAllNotifications();

    const isFilteringByUnread = isFilteringByUnreadNotifications();

    setAllNotificationsReadSuccess({ isFilteringByUnread });

    setNotificationSeenStateGroupCount(notificationsState.value.unreadCount);
  } catch (err) {
    setAllNotificationsReadError({
      unreadCount: unreadCountGroup,
      ids: idUnreadNotificationGroups,
    });
    throw err;
  }
};

const getNotificationsByGroupId = (idNotificationGroup: string) => {
  const notificationGroup = notificationsState.value.notificationGroups.find(
    (ng) => ng.id === idNotificationGroup,
  );

  return notificationGroup ? notificationGroup.notifications : [];
};

export const setNotificationGroupRead = async ({
  idGroup,
  read,
  notificationIds,
}: {
  idGroup: string;
  read: boolean;
  notificationIds?: string[];
}) => {
  const id = convertIdGroupToId(idGroup);

  setNotificationGroupReadRequest({
    idGroup,
    read,
    notificationIds,
  });

  const ids = notificationIds || getNotificationsByGroupId(id).map((n) => n.id);

  try {
    await readAllNotifications({ ids: ids.join(','), read });
  } catch (err) {
    setNotificationGroupReadRequest({
      idGroup,
      read: !read,
      notificationIds,
    }); // undo the change
    throw err;
  }
  const isFilteringByUnread = isFilteringByUnreadNotifications();

  setNotificationGroupReadSuccess({
    idGroup,
    read,
    isFilteringByUnread,
  });

  setNotificationSeenStateGroupCount(notificationsState.value.unreadCount);
};
